import React from 'react';
import { SlidingPanel } from '../primitives';
import './CaseCreator.css';

/**
 * Slot-based layout component for the Case Creator
 * Provides a flexible structure with Form, Panel, and Footer slots
 *
 * Usage:
 * <CaseCreatorLayout>
 *   <CaseCreatorLayout.Form>
 *     <YourFormComponent />
 *   </CaseCreatorLayout.Form>
 *
 *   <CaseCreatorLayout.Panel isOpen={isPanelOpen} onClose={handleClose}>
 *     <YourPanelContent />
 *   </CaseCreatorLayout.Panel>
 *
 *   <CaseCreatorLayout.Footer>
 *     <YourFooterContent />
 *   </CaseCreatorLayout.Footer>
 * </CaseCreatorLayout>
 */
const CaseCreatorLayout = ({ children, className = '' }) => {
  // Extract slot children
  const slots = {
    header: null,
    form: null,
    panel: null,
    footer: null,
  };

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;

    if (child.type === CaseCreatorLayout.Header) {
      slots.header = child;
    } else if (child.type === CaseCreatorLayout.Form) {
      slots.form = child;
    } else if (child.type === CaseCreatorLayout.Panel) {
      slots.panel = child;
    } else if (child.type === CaseCreatorLayout.Footer) {
      slots.footer = child;
    }
  });

  return (
    <div className={`case-creator-layout ${className}`}>
      {slots.header}
      <div className="case-creator-body">
        <div className="case-creator-main">
          {slots.form}
        </div>
        {slots.panel}
      </div>
      {slots.footer}
    </div>
  );
};

/**
 * Header slot - for page title, breadcrumbs, etc.
 */
CaseCreatorLayout.Header = ({ children, className = '' }) => (
  <header className={`case-creator-header ${className}`}>
    {children}
  </header>
);
CaseCreatorLayout.Header.displayName = 'CaseCreatorLayout.Header';

/**
 * Form slot - main content area for the form
 */
CaseCreatorLayout.Form = ({ children, className = '' }) => (
  <div className={`case-creator-form ${className}`}>
    {children}
  </div>
);
CaseCreatorLayout.Form.displayName = 'CaseCreatorLayout.Form';

/**
 * Panel slot - sliding panel for secondary content
 * Wraps children in the SlidingPanel primitive
 */
CaseCreatorLayout.Panel = ({
  children,
  isOpen = false,
  onClose,
  title,
  position = 'right',
  width = 400,
  showOverlay = true,
  className = '',
}) => (
  <SlidingPanel
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    position={position}
    width={width}
    showOverlay={showOverlay}
    className={className}
  >
    {children}
  </SlidingPanel>
);
CaseCreatorLayout.Panel.displayName = 'CaseCreatorLayout.Panel';

/**
 * Footer slot - for actions, status messages, etc.
 */
CaseCreatorLayout.Footer = ({ children, className = '' }) => (
  <footer className={`case-creator-footer ${className}`}>
    {children}
  </footer>
);
CaseCreatorLayout.Footer.displayName = 'CaseCreatorLayout.Footer';

export default CaseCreatorLayout;
